import React, { useEffect, useState } from "react";
import { auditService } from "../../services/api/audit.service";
import type { AuditLog } from "../../types/audit.types";
import "../../styles/pages/History.css";

interface HistoryProps {
  entityName: string;
  recordId?: number;
  isOpen: boolean;
  onClose: () => void;
}

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const History: React.FC<HistoryProps> = ({
  entityName,
  recordId,
  isOpen,
  onClose,
}) => {
  const [history, setHistory] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !recordId) return;

    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError("");
        const result = await auditService.getHistory(entityName, recordId);
        setHistory(Array.isArray(result) ? result : []);
      } catch (err: any) {
        setError(err?.message || "Failed to load history");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [entityName, recordId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="history-drawer-overlay" onClick={onClose}>
      <div
        className="history-drawer-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="history-drawer-header">
          <h3 className="history-drawer-title">History</h3>
          <button
            type="button"
            className="history-drawer-close"
            onClick={onClose}
            aria-label="Close history"
          >
            ✕
          </button>
        </div>

        <div className="history-drawer-body">
          {loading && (
            <div className="history-drawer-state">Loading history...</div>
          )}
          {!loading && error && (
            <div className="history-drawer-state history-drawer-state--error">
              {error}
            </div>
          )}
          {!loading && !error && history.length === 0 && (
            <div className="history-drawer-state">No history found.</div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Action</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td>{item.fullName || item.userName || "—"}</td>
                      <td>{item.userAction || "—"}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default History;
